import React from 'react';
import { NodeProps, useReactFlow } from 'reactflow';
import { TextInput } from '../../../../form/textInput';
import { operationType, selectDataType } from '../../../../../services/types';
import { updateNodeValue } from '../../../../../services/method';
import { Textarea } from '../../../../form/textarea';
import { Select } from '../../../../form/select';
import { Main, Styled } from './style';
import { AuthenticateSelect } from '../../../../auth/select';
import { IoCheckmarkCircleOutline, IoWarningOutline } from 'react-icons/io5';
import { SettingsBlock, settingsDataType } from '../../../../settingsBlock';
import { ExpandedBlock } from '../../../expanded';

type EasyOrdersProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

type EasyOrdersResource = 'product' | 'category' | 'order' | 'shipping';
type EasyOrdersRequest =
   | 'createProduct'
   | 'updateProduct'
   | 'getProduct'
   | 'getProducts'
   | 'updateProductStock'
   | 'updateVariantStock'
   | 'createCategory'
   | 'updateCategory'
   | 'getCategory'
   | 'getCategories'
   | 'getOrder'
   | 'updateOrderStatus'
   | 'addOrderNote'
   | 'updateShipping';

const resourceOptionsBase: selectDataType[] = [
   { id: 1, name: 'Product', refId: 'product' },
   { id: 2, name: 'Category', refId: 'category' },
   { id: 3, name: 'Order', refId: 'order' },
   { id: 4, name: 'Shipping', refId: 'shipping' },
];

const requestOptionsByResource: Record<EasyOrdersResource, selectDataType[]> = {
   product: [
      { id: 1, name: 'Create Product', refId: 'createProduct' },
      { id: 2, name: 'Update Product', refId: 'updateProduct' },
      { id: 3, name: 'Get Product', refId: 'getProduct' },
      { id: 4, name: 'Get Products', refId: 'getProducts' },
      { id: 5, name: 'Update Stock By SKU', refId: 'updateProductStock' },
      { id: 6, name: 'Update Variant Stock', refId: 'updateVariantStock' },
   ],
   category: [
      { id: 1, name: 'Create Category', refId: 'createCategory' },
      { id: 2, name: 'Update Category', refId: 'updateCategory' },
      { id: 3, name: 'Get Category', refId: 'getCategory' },
      { id: 4, name: 'Get Categories', refId: 'getCategories' },
   ],
   order: [
      { id: 1, name: 'Get Order', refId: 'getOrder' },
      { id: 2, name: 'Update Status', refId: 'updateOrderStatus' },
      { id: 3, name: 'Add Order Note', refId: 'addOrderNote' },
   ],
   shipping: [
      { id: 1, name: 'Update Shipping', refId: 'updateShipping' },
   ],
};

const defaultResource: EasyOrdersResource = 'product';
const defaultRequest = requestOptionsByResource[defaultResource][0].refId as string;

function stringifyTemplate(value: Record<string, any>) {
   return JSON.stringify(value, null, 2);
}

const payloadTemplates: Partial<Record<EasyOrdersRequest, string>> = {
   createProduct: stringifyTemplate({
      name: 'Sample Product',
      slug: 'sample-product',
      price: 200,
      sale_price: 180,
      thumb: 'https://app.easy-orders.net/rocket.png',
      sku: 'SAMPLE-001',
      stock: 25,
      description: 'Simple sample product created from the workflow node.',
      is_active: true,
   }),
   updateProduct: stringifyTemplate({
      name: 'Updated Product Name',
      slug: 'updated-product-name',
      price: 220,
      sale_price: 199,
      thumb: 'https://app.easy-orders.net/rocket.png',
      stock: 30,
      is_active: true,
   }),
   createCategory: stringifyTemplate({
      thumb: 'https://app.easy-orders.net/rocket.png',
      name: 'Summer Collection',
      slug: 'summer-collection',
      is_active: true,
   }),
   updateCategory: stringifyTemplate({
      thumb: 'https://app.easy-orders.net/rocket.png',
      name: 'Summer Collection Updated',
      slug: 'summer-collection-updated',
      is_active: true,
   }),
   addOrderNote: stringifyTemplate({
      order_id: '12345',
      note: 'Customer requested delivery after 6 PM.',
      store_id: 1,
      type: 'internal',
   }),
   updateShipping: stringifyTemplate({
      is_active: true,
      cities: 'cairo:30,alexandria:40,giza:35',
   }),
};

const queryTemplates: Partial<Record<EasyOrdersRequest, string>> = {
   getProduct: stringifyTemplate({
      
   }),
   getProducts: stringifyTemplate({
      page: 1,
      limit: 20,
      filter: 'is_active||eq||true',
      join: 'Category',
   }),
   getCategory: stringifyTemplate({
      join: 'Products',
   }),
   getCategories: stringifyTemplate({
      page: 1,
      limit: 20,
      filter: 'is_active||eq||true',
   }),
   getOrder: stringifyTemplate({
   }),
};

function getPayloadTemplate(request?: string) {
   return payloadTemplates[request as EasyOrdersRequest] || '';
}

function getQueryTemplate(request?: string) {
   return queryTemplates[request as EasyOrdersRequest] || '';
}

function getResourceFromRequest(request?: string): EasyOrdersResource {
   if (!request) return defaultResource;
   for (const resource of Object.keys(requestOptionsByResource) as EasyOrdersResource[]) {
      if (requestOptionsByResource[resource].some(option => option.refId === request)) {
         return resource;
      }
   }
   return defaultResource;
}

const functionDocs: Record<string, string> = {
   createProduct: `Required payload fields:
- name
- slug
- price
- thumb

Recommended fields:
- sale_price
- sku
- stock
- description
- is_active`,
   updateProduct: `Requires Product ID and payload JSON object.

Common fields:
- name
- slug
- price
- sale_price
- thumb
- stock
- is_active`,
   getProduct: `Requires Product ID.

Optional query fields:
- join

Example:
{
  "join": "Variations.Props,Variants.VariationProps"
}`,
   getProducts: `Optional query fields:
- page
- limit
- filter
- join

Use Easy Orders filter syntax like "is_active||eq||true".`,
   updateProductStock: `Requires:
- SKU
- Quantity

Sends PATCH /products/sku/:sku/quantity with:
{
  "quantity": 10
}`,
   updateVariantStock: `Requires:
- Product Taager Code
- Variant Taager Code
- Quantity

Sends:
{
  "quantity": 10
}`,
   createCategory: `Required payload fields:
- thumb
- name
- slug

Recommended:
- is_active`,
   updateCategory: `Requires Category ID and payload JSON object.

Common fields:
- thumb
- name
- slug
- is_active`,
   getCategory: `Requires Category ID.

Optional query:
{
  "join": "Products"
}`,
   getCategories: `Optional query fields:
- page
- limit
- filter`,
   getOrder: `Requires Order ID.

Optional query:
{
}`,
   updateOrderStatus: `Requires Order ID and status.

Example:
{
  "status": "processing"
}`,
   addOrderNote: `Payload must include:
- order_id
- note
- store_id
- type`,
   updateShipping: `Payload example:
{
  "is_active": true,
  "cities": "cairo:30,alexandria:40,giza:35"
}`,
};

export function EasyOrders({ id, data, setChildMinimize, setChildSettings }: EasyOrdersProps) {
   const { setNodes } = useReactFlow();
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);

   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev); });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })); });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   React.useEffect(() => {
      if (!data.value) {
         updateNodeValue(id, setNodes, {
            resource: defaultResource,
            request: defaultRequest,
            payload: getPayloadTemplate(defaultRequest),
            query: getQueryTemplate(defaultRequest),
         });
      } else if (!data.value?.resource && data.value?.request) {
         updateNodeValue(id, setNodes, { resource: getResourceFromRequest(data.value.request) });
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   const handleAuthenticate = async (type: 'add' | 'remove', connectionData: any) => {
      if (type === 'add') {
         updateNodeValue(id, setNodes, { connection: connectionData });
      } else {
         updateNodeValue(id, setNodes, { connection: null });
      }
   };

   const activeRequestValue = data.value?.request as string | undefined;
   const activeResourceValue = (data.value?.resource as EasyOrdersResource | undefined) || getResourceFromRequest(activeRequestValue);
   const requestOptionsForResource = requestOptionsByResource[activeResourceValue];
   const activeRequest = requestOptionsForResource.some(request => request.refId === activeRequestValue)
      ? (activeRequestValue as string)
      : (requestOptionsForResource[0]?.refId as string);

   const resourceOptions = resourceOptionsBase.map(option => ({ ...option, isActive: option.refId === activeResourceValue }));
   const actionOptions = requestOptionsForResource.map(option => ({ ...option, isActive: option.refId === activeRequest }));

   const handleSelectResource = (selectId: number) => {
      const nextResource = resourceOptionsBase.find(resource => resource.id === selectId)?.refId as EasyOrdersResource | undefined;
      if (!nextResource) return;
      const firstAction = requestOptionsByResource[nextResource][0]?.refId as string | undefined;
      updateNodeValue(id, setNodes, {
         resource: nextResource,
         request: firstAction,
         payload: getPayloadTemplate(firstAction),
         query: getQueryTemplate(firstAction),
      });
   };

   const handleSelectAction = (selectId: number) => {
      const refId = requestOptionsForResource.find(request => request.id === selectId)?.refId;
      if (!refId) return;
      updateNodeValue(id, setNodes, {
         resource: activeResourceValue,
         request: refId,
         payload: getPayloadTemplate(refId),
         query: getQueryTemplate(refId),
      });
   };

   const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string) => {
      if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: e.target.value });
   };

   const isProductIdRequest = ['updateProduct', 'getProduct'].includes(activeRequest);
   const isCategoryIdRequest = ['updateCategory', 'getCategory'].includes(activeRequest);
   const isOrderIdRequest = ['getOrder', 'updateOrderStatus'].includes(activeRequest);
   const isSkuRequest = activeRequest === 'updateProductStock';
   const isVariantStockRequest = activeRequest === 'updateVariantStock';
   const isQuantityRequest = ['updateProductStock', 'updateVariantStock'].includes(activeRequest);
   const isStatusRequest = activeRequest === 'updateOrderStatus';
   const isPayloadRequest = ['createProduct', 'updateProduct', 'createCategory', 'updateCategory', 'addOrderNote', 'updateShipping'].includes(activeRequest);
   const isQueryRequest = ['getProduct', 'getProducts', 'getCategory', 'getCategories', 'getOrder'].includes(activeRequest);
   const isWarning = !data.value?.connection;

   return (
      <>
         <SettingsBlock nodeId={ id } data={ settings } setData={ setSettings } />
         <Main>
            {isWarning ? (
               <>
                  <div id="warning"></div>
                  <span id="warningIcon"><IoWarningOutline size={21} /></span>
               </>
            ) : data.output ? (
               <>
                  <div id="ran"></div>
                  <span id="ranIcon"><IoCheckmarkCircleOutline size={ 21 } /></span>
               </>
            ) : null}

            {isExpanded ? (
               <ExpandedBlock
                  data={{ id, ...data }}
                  handleClose={ () => setIsExpanded(false) }
                  isWarning={ isWarning }
                  style={{ minWidth: '420px' }}
               >
                  <Styled>
                     <div className="action">
                        <AuthenticateSelect
                           type="easyOrders"
                           defaultId={ data.value?.connection?.id }
                           handleAuthenticate={ handleAuthenticate }
                           background={ data.background }
                        />
                     </div>

                     <Select title="Resource" data={ resourceOptions } handleSelectItem={ handleSelectResource } isSmall={ true } />
                     <Select title="Action" data={ actionOptions } handleSelectItem={ handleSelectAction } isSmall={ true } />

                     {isProductIdRequest ? (
                        <TextInput keyRef="productId" value={ data.value?.productId } title="Product ID" placeholder="product-id" handleChange={ handleChangeInput } />
                     ) : null}
                     {isCategoryIdRequest ? (
                        <TextInput keyRef="categoryId" value={ data.value?.categoryId } title="Category ID" placeholder="category-id" handleChange={ handleChangeInput } />
                     ) : null}
                     {isOrderIdRequest ? (
                        <TextInput keyRef="orderId" value={ data.value?.orderId } title="Order ID" placeholder="order-id" handleChange={ handleChangeInput } />
                     ) : null}
                     {isSkuRequest ? (
                        <TextInput keyRef="sku" value={ data.value?.sku } title="SKU" placeholder="sku-123" handleChange={ handleChangeInput } />
                     ) : null}
                     {isVariantStockRequest ? (
                        <>
                           <TextInput keyRef="productTaagerCode" value={ data.value?.productTaagerCode } title="Product SKU" placeholder="product-code" handleChange={ handleChangeInput } />
                           <TextInput keyRef="variantTaagerCode" value={ data.value?.variantTaagerCode } title="Variant SKU" placeholder="variant-code" handleChange={ handleChangeInput } />
                        </>
                     ) : null}
                     {isQuantityRequest ? (
                        <TextInput keyRef="quantity" value={ data.value?.quantity } title="Quantity" placeholder="10" handleChange={ handleChangeInput } />
                     ) : null}
                     {isStatusRequest ? (
                        <TextInput keyRef="status" value={ data.value?.status } title="Status" placeholder="processing" handleChange={ handleChangeInput } />
                     ) : null}

                     {isPayloadRequest ? (
                        <Textarea
                           title="Payload"
                           keyRef="payload"
                           value={ data.value?.payload }
                           placeholder={ getPayloadTemplate(activeRequest) || '{\n  "field": "value"\n}' }
                           handleChange={ (e) => updateNodeValue(id, setNodes, { payload: e.target.value }) }
                        />
                     ) : null}

                     {isQueryRequest ? (
                        <Textarea
                           title="Query"
                           keyRef="query"
                           value={ data.value?.query }
                           placeholder={ getQueryTemplate(activeRequest) || '{\n  "page": 1\n}' }
                           handleChange={ (e) => updateNodeValue(id, setNodes, { query: e.target.value }) }
                        />
                     ) : null}

                     <div className="previewSection">
                        <h5>Function Block Preview</h5>
                        <pre>{ functionDocs[activeRequest] || 'No function metadata available.' }</pre>
                     </div>
                  </Styled>
               </ExpandedBlock>
            ) : null}
         </Main>
      </>
   );
}
